import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useAgency } from '../context/AgencyContext';
import AgencySwitcher from './AgencySwitcher';
import NotificationPanel from './NotificationPanel';
import useErrorReportPoller from '../hooks/useErrorReportPoller';
import {
  House,
  Users,
  Archive,
  FileText,
  FilePlus,
  BookmarkSimple,
  ListChecks,
  SignOut,
  List,
  X,
  CaretDown,
  CaretRight,
  UserCircle,
  NavigationArrow,
  Buildings,
  Plugs,
  FileArrowDown,
  Brain,
  ChartBar,
  Shield,
  ClockCounterClockwise,
  Storefront,
  Receipt,
  UserPlus,
  ChartLine,
  CloudArrowUp,
  PaperPlaneTilt,
  Wrench,
  CurrencyDollar,
  Folder,
  GearSix,
  EnvelopeSimple,
  Scan,
  WarningCircle,
  ShieldCheck,
  Heart,
  Certificate,
  Play,
} from '@phosphor-icons/react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Button } from './ui/button';
import OdmEnvBadge from './OdmEnvBadge';
import { SandataModeBadge } from './SandataModeBadge';
import { ElevationBanner, ElevationDialog } from './PlatformElevation';
import { ApprovalsBadge } from './ApprovalsBadge';

const navGroups = [
  {
    items: [
      { icon: House, label: 'Dashboard', path: '/dashboard' },
    ],
  },
  {
    label: 'People',
    icon: Users,
    items: [
      { icon: Users, label: 'Patients', path: '/patients' },
      { icon: UserCircle, label: 'Employees', path: '/employees' },
    ],
  },
  {
    // P36 (Feb 28, 2026) — unified Billing Hub. Collapsed the 6-item
    // billing group (Claims / Denials / New Claim / ODM / BH / DODD)
    // into a single top-level link that opens /billing — a hub page
    // with quick stats and section cards for each sub-area. The
    // individual pages remain reachable at their existing routes.
    items: [
      { icon: CurrencyDollar, label: 'Billing', path: '/billing' },
    ],
  },
  {
    label: 'EVV',
    icon: NavigationArrow,
    items: [
      { icon: NavigationArrow, label: 'Visits', path: '/evv' },
      { icon: Wrench, label: 'Resolution Center', path: '/evv/resolution' },
      { icon: Scan, label: 'Timesheet Scanner', path: '/timesheets' },
    ],
  },
  {
    label: 'Submissions',
    icon: CloudArrowUp,
    items: [
      { icon: PaperPlaneTilt, label: 'ODM Submissions', path: '/odm/submissions' },
      { icon: WarningCircle, label: 'Manual Review Queue', path: '/admin/manual-review' },
      { icon: Plugs, label: 'Integrations', path: '/integrations' },
      { icon: ListChecks, label: 'Sandata Certification', path: '/sandata-cert' },
    ],
  },
  {
    label: 'Reference',
    icon: Folder,
    items: [
      { icon: CurrencyDollar, label: 'Payer Contracts', path: '/payer-contracts' },
      { icon: ListChecks, label: 'HCPCS Codes', path: '/codes' },
      { icon: BookmarkSimple, label: 'Templates', path: '/templates' },
      { icon: ChartBar, label: 'Reports', path: '/reports' },
      { icon: Receipt, label: 'Billing History', path: '/billing/history' },
    ],
  },
  {
    label: 'Admin',
    icon: GearSix,
    items: [
      { icon: Shield, label: 'User Management', path: '/admin/users' },
      { icon: UserPlus, label: 'Staff Invitations', path: '/admin/invitations' },
      { icon: ClockCounterClockwise, label: 'Audit Logs', path: '/admin/audit' },
      { icon: Archive, label: 'Retention Console', path: '/admin/retention' },
    ],
  },
  {
    label: 'AZAI Admin',
    icon: Storefront,
    azaiOnly: true,
    items: [
      { icon: Storefront, label: 'Agencies', path: '/admin/agencies' },
      { icon: GearSix, label: 'Agency Settings', path: '/admin/agency-settings' },
      { icon: Receipt, label: 'AZAI Billing', path: '/admin/billing' },
      { icon: ChartLine, label: 'Cross-Agency Reports', path: '/admin/cross-agency' },
      { icon: EnvelopeSimple, label: 'Email Audit Log', path: '/admin/email-log' },
      {
        icon: ShieldCheck,
        label: 'Elevation Approvals',
        path: '/admin/security/elevation-approvals',
        badge: 'approvals-pending-count',
      },
      { icon: PaperPlaneTilt, label: 'ODM Certification', path: '/admin/odm-cert' },
      { icon: Certificate, label: '270 CERT Package', path: '/admin/odm-cert/270-package' },
      { icon: Play, label: 'Certification Runner', path: '/admin/odm-cert/runner' },
      { icon: ShieldCheck, label: 'ODM Credentials', path: '/admin/odm-credentials' },
      { icon: Heart, label: 'Eligibility Batches', path: '/admin/eligibility-batches' },
    ],
  },
];

const NavGroup = ({ group, isAzaiStaff, location, onNav }) => {
  const hasActiveChild = group.items.some((item) => location.pathname === item.path);
  const [open, setOpen] = useState(hasActiveChild);

  // Standalone items (no label = top-level, e.g. Dashboard)
  if (!group.label) {
    return group.items.map((item) => (
      <NavLink key={item.path} item={item} active={location.pathname === item.path} onNav={onNav} />
    ));
  }

  if (group.azaiOnly && !isAzaiStaff) return null;

  return (
    <div className="mb-1">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-2 px-3 py-2 text-xs font-semibold uppercase tracking-wider text-slate-400 hover:text-slate-600 transition-colors rounded-md"
        data-testid={`nav-group-${slugify(group.label)}`}
      >
        <group.icon size={14} weight="bold" />
        <span className="flex-1 text-left">{group.label}</span>
        <CaretRight
          size={12}
          className={`transition-transform duration-200 ${open ? 'rotate-90' : ''}`}
        />
      </button>
      {open && (
        <div className="ml-2 space-y-0.5 mt-0.5">
          {group.items.map((item) => (
            <NavLink key={item.path} item={item} active={location.pathname === item.path} onNav={onNav} />
          ))}
        </div>
      )}
    </div>
  );
};

const slugify = (label) =>
  label.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

const NavLink = ({ item, active, onNav }) => (
  <Link
    to={item.path}
    className={`nav-link ${active ? 'active' : ''}`}
    onClick={onNav}
    data-testid={`nav-${slugify(item.label)}`}
  >
    <item.icon size={18} weight={active ? 'fill' : 'regular'} />
    <span className="text-sm flex-1">{item.label}</span>
    {item.badge === 'approvals-pending-count' && <ApprovalsBadge />}
  </Link>
);

const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const { isAzaiStaff } = useAgency();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // P53.A — toast AZAI staff when ODM drops a new HTML ErrorReport on
  // the active env. No-op for non-AZAI users.
  useErrorReportPoller(Boolean(isAzaiStaff));

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/30 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <aside
        className={`fixed top-0 left-0 z-50 h-full w-56 bg-white border-r border-slate-200 transform transition-transform duration-200 lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between h-14 px-4 border-b border-slate-200">
            <Link to="/dashboard" className="flex items-center gap-2">
              <div className="w-7 h-7 bg-indigo-900 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-xs">AZ</span>
              </div>
              <span className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
                AZAI Billing
              </span>
            </Link>
            <button
              className="lg:hidden p-1 hover:bg-slate-100 rounded"
              onClick={() => setSidebarOpen(false)}
            >
              <X size={18} className="text-slate-500" />
            </button>
          </div>

          <nav className="flex-1 px-2 py-3 space-y-0.5 overflow-y-auto">
            {navGroups.map((group, idx) => (
              <NavGroup
                key={group.label || idx}
                group={group}
                isAzaiStaff={isAzaiStaff}
                location={location}
                onNav={() => setSidebarOpen(false)}
              />
            ))}
          </nav>

          <div className="p-3 border-t border-slate-200">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="w-full flex items-center gap-2 p-2 rounded-lg hover:bg-slate-100 transition-colors">
                  <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center">
                    <span className="text-indigo-900 font-semibold text-xs">
                      {user?.name?.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div className="flex-1 text-left min-w-0">
                    <p className="text-sm font-medium text-slate-900 truncate">{user?.name}</p>
                    <p className="text-xs text-slate-500 truncate">{user?.agency_name || 'No Agency'}</p>
                  </div>
                  <CaretDown size={14} className="text-slate-400 shrink-0" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <div className="px-2 py-1.5">
                  <p className="text-sm font-medium">{user?.name}</p>
                  <p className="text-xs text-slate-500">{user?.email}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => navigate('/account/mfa')}
                  className="cursor-pointer"
                  data-testid="user-menu-account-security"
                >
                  <ShieldCheck size={16} className="mr-2" />
                  Account Security
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleLogout} className="text-red-600 cursor-pointer" data-testid="user-menu-signout">
                  <SignOut size={16} className="mr-2" />
                  Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </aside>

      <div className="lg:pl-56">
        <ElevationBanner isAzaiStaff={Boolean(isAzaiStaff)} />
        <ElevationDialog />
        <header className="sticky top-0 z-30 h-14 bg-white border-b border-slate-200 flex items-center px-4 lg:px-8">
          <button
            className="lg:hidden p-2 hover:bg-slate-100 rounded-lg mr-4"
            onClick={() => setSidebarOpen(true)}
            data-testid="mobile-menu-btn"
          >
            <List size={22} className="text-slate-600" />
          </button>
          <div className="hidden md:block">
            <AgencySwitcher />
          </div>
          <div className="flex-1" />
          <SandataModeBadge />
          <OdmEnvBadge />
          <NotificationPanel />
          <Button
            onClick={() => navigate('/claims/new')}
            className="bg-indigo-900 hover:bg-indigo-800 h-9 text-sm"
            data-testid="new-claim-header-btn"
          >
            <FilePlus size={16} className="mr-1.5" />
            New Claim
          </Button>
        </header>
        <main className="p-4 lg:p-8">{children}</main>
      </div>
    </div>
  );
};

export default Layout;
